import React, { useMemo, useState } from "react";
import AddQuestionModal from "./AddQuestionModal";

const SECTIONS = ["Pharmacy Knowledge", "Customer Handling", "Situational Questions"];
const TABS = { PARAGRAPHS: "paragraphs", QUESTIONS: "questions" };

const AdminQuestions = ({
  questions,
  setQuestions,
  questionLimit,
  onQuestionLimitChange,
  typingPassages,
  onTypingPassagesChange,
  onSave,
  isSaving,
  t,
}) => {
  const [activeTab, setActiveTab] = useState(TABS.PARAGRAPHS);
  const [search, setSearch] = useState("");
  const [sectionFilter, setSectionFilter] = useState("all");
  const [expandedIndex, setExpandedIndex] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [limitError, setLimitError] = useState("");
  const [passageError, setPassageError] = useState("");

  const filledPassageCount = typingPassages.filter((p) => p.trim()).length;

  const filteredIndices = useMemo(() => {
    return questions
      .map((q, i) => ({ q, i }))
      .filter(({ q }) => {
        const matchesSection = sectionFilter === "all" || q.section === sectionFilter;
        const searchLower = search.toLowerCase();
        const matchesSearch = !search || q.en?.question?.toLowerCase().includes(searchLower);
        return matchesSection && matchesSearch;
      })
      .map(({ i }) => i);
  }, [questions, search, sectionFilter]);

  const handleAddQuestion = (newQ) => {
    setQuestions([...questions, newQ]);
    setExpandedIndex(null);
    setActiveTab(TABS.QUESTIONS);
  };

  const handleQChange = (index, field, value) => {
    const updated = [...questions];
    updated[index].en[field] = value;
    setQuestions(updated);
  };

  const handleOptionChange = (qIndex, optIndex, value) => {
    const updated = [...questions];
    const oldVal = updated[qIndex].en.options[optIndex];
    updated[qIndex].en.options[optIndex] = value;
    if (updated[qIndex].en.answer === oldVal) {
      updated[qIndex].en.answer = value;
    }
    setQuestions(updated);
  };

  const handleTypeChange = (index, type) => {
    const updated = [...questions];
    updated[index].type = type;
    if (type === "text") {
      updated[index].en.options = [];
      updated[index].en.answer = "";
    } else if (updated[index].en.options.length === 0) {
      updated[index].en.options = ["", "", "", ""];
    }
    setQuestions(updated);
  };

  const confirmDelete = (index) => {
    setQuestions(questions.filter((_, i) => i !== index));
    setDeleteConfirm(null);
    if (expandedIndex === index) setExpandedIndex(null);
  };

  const handleLimitChange = (e) => {
    const digits = e.target.value.replace(/\D/g, "");
    onQuestionLimitChange(digits);
    if (limitError) setLimitError("");
  };

  const handleSave = () => {
    const limit = Number(questionLimit);
    if (!questionLimit || limit < 1) {
      setLimitError(t.questionLimitInvalid);
      setActiveTab(TABS.QUESTIONS);
      return;
    }
    setLimitError("");

    const cleanedPassages = typingPassages.map((p) => p.trim()).filter(Boolean);
    if (!cleanedPassages.length) {
      setPassageError(t.typingPassageRequired);
      setActiveTab(TABS.PARAGRAPHS);
      return;
    }
    setPassageError("");

    onSave([...questions], limit, cleanedPassages);
  };

  const handlePassageChange = (index, value) => {
    const updated = [...typingPassages];
    updated[index] = value;
    onTypingPassagesChange(updated);
    if (passageError) setPassageError("");
  };

  const addPassage = () => {
    onTypingPassagesChange([...typingPassages, ""]);
  };

  const removePassage = (index) => {
    if (typingPassages.length <= 1) return;
    onTypingPassagesChange(typingPassages.filter((_, i) => i !== index));
  };

  return (
    <div className="admin-questions mq-page">
      <div className="mq-topbar">
        <div className="mq-tabs" role="tablist" aria-label={t.manageQuestions}>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === TABS.PARAGRAPHS}
            className={`mq-tab ${activeTab === TABS.PARAGRAPHS ? "active" : ""}`}
            onClick={() => setActiveTab(TABS.PARAGRAPHS)}
          >
            <span className="mq-tab-icon" aria-hidden="true">⌨️</span>
            {t.paragraphsTab}
            <span className="mq-tab-count">{filledPassageCount}</span>
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === TABS.QUESTIONS}
            className={`mq-tab ${activeTab === TABS.QUESTIONS ? "active" : ""}`}
            onClick={() => setActiveTab(TABS.QUESTIONS)}
          >
            <span className="mq-tab-icon" aria-hidden="true">📝</span>
            {t.questionsTab}
            <span className="mq-tab-count">{questions.length}</span>
          </button>
        </div>
        <button
          type="button"
          onClick={handleSave}
          disabled={isSaving}
          className="admin-btn admin-btn-primary mq-save-btn"
        >
          <span className="admin-btn-icon">💾</span>
          {isSaving ? t.saving : t.saveAll}
        </button>
      </div>

      {activeTab === TABS.PARAGRAPHS && (
        <div className="mq-panel" role="tabpanel">
          <div className="mq-panel-banner mq-panel-banner-teal">
            <div>
              <h3 className="mq-panel-title">{t.typingPassagesConfig}</h3>
              <p className="mq-panel-desc">{t.typingPassagesHint}</p>
            </div>
            <button
              type="button"
              className="admin-btn admin-btn-primary"
              onClick={addPassage}
            >
              <span className="admin-btn-icon">＋</span>
              {t.addTypingPassage}
            </button>
          </div>

          {passageError && <div className="mq-alert mq-alert-error">{passageError}</div>}

          <div className="mq-passage-grid">
            {typingPassages.map((passage, index) => (
              <div key={index} className="mq-passage-card">
                <div className="mq-passage-card-head">
                  <span className="mq-passage-num">{t.typingPassageLabel} {index + 1}</span>
                  <div className="mq-passage-meta">
                    <span className="mq-char-count">{passage.trim().length} chars</span>
                    {typingPassages.length > 1 && (
                      <button
                        type="button"
                        className="mq-icon-btn mq-icon-btn-danger"
                        onClick={() => removePassage(index)}
                        aria-label={t.deleteQuestion}
                      >
                        🗑️
                      </button>
                    )}
                  </div>
                </div>
                <textarea
                  className="mq-passage-input"
                  value={passage}
                  onChange={(e) => handlePassageChange(index, e.target.value)}
                  rows={4}
                  placeholder={t.typingPassagePlaceholder}
                />
              </div>
            ))}
          </div>

          <button type="button" className="mq-add-card" onClick={addPassage}>
            <span className="mq-add-card-icon">＋</span>
            <span>{t.addTypingPassage}</span>
            <span className="mq-add-card-sub">
              {typingPassages.length} {t.paragraphsUsed}
            </span>
          </button>
        </div>
      )}

      {activeTab === TABS.QUESTIONS && (
        <div className="mq-panel" role="tabpanel">
          <div className="mq-limit-card">
            <div className="mq-limit-info">
              <span className="mq-limit-icon" aria-hidden="true">⚙️</span>
              <div>
                <h3 className="mq-panel-title">{t.questionLimit}</h3>
                <p className="mq-panel-desc">{t.questionLimitHint}</p>
              </div>
            </div>
            <div className="mq-limit-field">
              <input
                id="question-limit"
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                value={questionLimit}
                onChange={handleLimitChange}
                placeholder="15"
                maxLength={3}
                aria-label={t.questionLimit}
              />
              {limitError && <span className="admin-config-error">{limitError}</span>}
            </div>
          </div>

          <div className="mq-toolbar">
            <div className="mq-toolbar-filters">
              <input
                type="search"
                placeholder={t.searchQuestions}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="mq-search"
              />
              <select value={sectionFilter} onChange={(e) => setSectionFilter(e.target.value)}>
                <option value="all">{t.allSections}</option>
                {SECTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <button type="button" onClick={() => setShowAddModal(true)} className="admin-btn admin-btn-outline">
              <span className="admin-btn-icon">＋</span>
              {t.addQuestion}
            </button>
          </div>

          <div className="questions-list mq-questions-list">
            {filteredIndices.length === 0 ? (
              <div className="mq-empty">
                <span className="mq-empty-icon">📝</span>
                <p>{t.noMatchingQuestions}</p>
                <button type="button" className="admin-btn admin-btn-outline" onClick={() => setShowAddModal(true)}>
                  {t.addQuestion}
                </button>
              </div>
            ) : (
              filteredIndices.map((qIdx) => {
                const q = questions[qIdx];
                const isExpanded = expandedIndex === qIdx;
                return (
                  <div key={q._id || qIdx} className={`question-admin-card ${isExpanded ? "expanded" : ""}`}>
                    <button
                      type="button"
                      className="question-admin-header"
                      onClick={() => setExpandedIndex(isExpanded ? null : qIdx)}
                    >
                      <span className="question-admin-num">Q{qIdx + 1}</span>
                      <span className="question-admin-preview">
                        {q.en?.question || t.emptyQuestion}
                      </span>
                      <span className="question-admin-section">{q.section}</span>
                      <span className="question-admin-chevron">{isExpanded ? "▲" : "▼"}</span>
                    </button>

                    {isExpanded && (
                      <div className="question-admin-body">
                        <div className="question-type-row">
                          <select
                            value={q.section}
                            onChange={(e) => {
                              const updated = [...questions];
                              updated[qIdx].section = e.target.value;
                              setQuestions(updated);
                            }}
                          >
                            {SECTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                          </select>
                          <select value={q.type || "mcq"} onChange={(e) => handleTypeChange(qIdx, e.target.value)}>
                            <option value="mcq">{t.mcq}</option>
                            <option value="text">{t.text}</option>
                          </select>
                        </div>

                        <div className="admin-question-single">
                          <input
                            placeholder="Question"
                            value={q.en.question}
                            onChange={(e) => handleQChange(qIdx, "question", e.target.value)}
                          />
                          {q.type !== "text" && q.en.options.map((opt, oIdx) => (
                            <div key={oIdx} className="option-edit-row">
                              <span className="option-edit-label">{String.fromCharCode(65 + oIdx)}</span>
                              <input
                                placeholder={`Option ${oIdx + 1}`}
                                value={opt}
                                onChange={(e) => handleOptionChange(qIdx, oIdx, e.target.value)}
                              />
                            </div>
                          ))}
                          {q.type !== "text" && (
                            <div className="correct-answer-row">
                              <label>{t.correctAnswer}</label>
                              <select
                                value={q.en.answer}
                                onChange={(e) => handleQChange(qIdx, "answer", e.target.value)}
                              >
                                <option value="">{t.selectCorrect}</option>
                                {q.en.options.map((opt, oIdx) => (
                                  <option key={oIdx} value={opt}>
                                    {String.fromCharCode(65 + oIdx)}: {opt || "(empty)"}
                                  </option>
                                ))}
                              </select>
                            </div>
                          )}
                        </div>

                        <div className="question-admin-footer">
                          {deleteConfirm === qIdx ? (
                            <div className="delete-confirm">
                              <div className="delete-confirm-copy">
                                <span className="delete-confirm-icon" aria-hidden="true">🗑️</span>
                                <p>{t.confirmDelete}</p>
                              </div>
                              <div className="delete-confirm-actions">
                                <button type="button" className="admin-btn admin-btn-ghost" onClick={() => setDeleteConfirm(null)}>
                                  {t.no}
                                </button>
                                <button type="button" className="admin-btn admin-btn-danger" onClick={() => confirmDelete(qIdx)}>
                                  {t.yes}, {t.deleteQuestion}
                                </button>
                              </div>
                            </div>
                          ) : (
                            <button
                              type="button"
                              className="admin-btn admin-btn-danger-outline"
                              onClick={() => setDeleteConfirm(qIdx)}
                            >
                              <span className="admin-btn-icon" aria-hidden="true">🗑️</span>
                              {t.deleteQuestion}
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}

      {showAddModal && (
        <AddQuestionModal
          t={t}
          onClose={() => setShowAddModal(false)}
          onAdd={handleAddQuestion}
        />
      )}
    </div>
  );
};

export default AdminQuestions;
